import { defineMessages } from 'react-intl';

const _default_1: {
	unsupportedFile: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	unknownError: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	fileRaiPolicyViolationError: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	failToValidateFilesTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	failToValidateFilesDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	invalidFilesTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	invalidFilesDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	fileInvalidIconLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	validationErrorInvalidContentType: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	validationErrorFileTooLarge: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	validationErrorFileNameMissing: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	validationErrorUnknownError: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	validationErrorMissingErrorCode: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	validationErrorNotFound: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	unsupportedFile: {
		id: 'ai-mate.upload-files-to-conversation.unsupported-file',
		defaultMessage: 'Unsupported file type',
		description:
			'Error message displayed in a notification or alert when a user attempts to upload a file with an unsupported file type to the AI conversation. Shown as an error message to inform the user that the selected file format cannot be processed. The purpose is to clearly communicate why the file upload was rejected.',
	},
	unknownError: {
		id: 'ai-mate.upload-files-to-conversation.unknown-error',
		defaultMessage: 'There was an error uploading the file. Please try again.',
		description:
			'Error message displayed in a notification or alert when an unexpected error occurs during file upload to the AI conversation. Shown as an error message to inform the user that the upload failed for an unknown reason and prompts them to retry the action. The purpose is to provide a user-friendly error message when the system cannot determine the specific cause of the upload failure.',
	},
	fileRaiPolicyViolationError: {
		id: 'ai-mate.upload-files-to-conversation.file-rai-policy-violation',
		defaultMessage:
			'The file might not comply with our Acceptable Use Policy. Try a different file.',
		description:
			'Error message displayed in a notification or alert when a user attempts to upload a file that potentially violates the Responsible AI (RAI) Acceptable Use Policy. Shown as an error message to inform the user that the file may contain content that does not meet policy requirements and prompts them to select a different file. The purpose is to guide users to comply with content policies while maintaining a helpful tone.',
	},
	failToValidateFilesTitle: {
		id: 'ai-mate.upload-files-to-conversation.fail-to-validate-files-title',
		defaultMessage: 'Failed to validate files',
		description:
			'Title text displayed at the top of an error dialog or notification when file validation fails during the upload process in the AI conversation. Shown as a dialog title to summarize the validation failure. The purpose is to immediately communicate that the validation process encountered an error.',
	},
	failToValidateFilesDescription: {
		id: 'ai-mate.upload-files-to-conversation.fail-to-validate-files-description',
		defaultMessage: 'Error occurred while validating files, please try again.',
		description:
			'Description text displayed in an error dialog or notification body when file validation fails during upload to the AI conversation. Shown as error message text below the title to explain that a validation error occurred and prompts the user to retry. The purpose is to provide context about the failure and guide the user to attempt the upload again.',
	},
	invalidFilesTitle: {
		id: 'ai-mate.upload-files-to-conversation.refresh.invalid-files-title',
		defaultMessage: '{count, plural, one {File type} other {File types}} not supported',
		description:
			'Title text displayed at the top of an error dialog or notification when one or more uploaded files have unsupported file types. Shown as a dialog title. The ICU MessageFormat plural statement resolves to "File type" when count is 1, or "File types" when count is 2 or more. The count placeholder will be substituted with the number of invalid files. The purpose is to indicate how many files have unsupported types.',
	},
	invalidFilesDescription: {
		id: 'ai-mate.upload-files-to-conversation.refresh.invalid-files-description',
		defaultMessage:
			'{count, plural, one {The file "{fileNames}" is not supported. Reason: {reasons}} other {The following files: "{fileNames}" are not supported. Reason: {reasons}}}',
		description:
			'Description text displayed in an error dialog or notification body explaining why specific files are invalid. Shown as error message text below the title. The ICU MessageFormat plural statement resolves to a singular message when count is 1, or a plural message when count is 2 or more. The count placeholder will be substituted with the number of invalid files. The fileNames placeholder will be substituted with a comma-separated list of file names (e.g., "document.pdf, image.jpg"). The reasons placeholder will be substituted with the validation failure reasons (e.g., "File type not supported, File too large"). The purpose is to provide detailed information about which files failed validation and why.',
	},
	fileInvalidIconLabel: {
		id: 'ai-mate.upload-files-to-conversation.file-invalid-icon-label',
		defaultMessage: 'File is invalid',
		description:
			'Accessibility label (aria-label) for an icon displayed next to invalid files in the file upload interface within the AI conversation. Shown as a screen reader announcement when the icon is focused or hovered. The purpose is to provide accessible information about the file validation status for users with assistive technologies.',
	},
	validationErrorInvalidContentType: {
		id: 'ai-mate.upload-files-to-conversation.validation-error-invalid-content-type',
		defaultMessage: 'File type is not supported',
		description:
			'Error message displayed in a notification or validation error list when a user attempts to upload a file with an unsupported MIME type or file extension to the AI conversation. Shown as an error message to inform the user that the file format is not accepted. The purpose is to clearly communicate that the file type is the reason for the validation failure.',
	},
	validationErrorFileTooLarge: {
		id: 'ai-mate.upload-files-to-conversation.validation-error-file-too-large',
		defaultMessage: 'File size is too large',
		description:
			'Error message displayed in a notification or validation error list when a user attempts to upload a file that exceeds the maximum allowed file size limit to the AI conversation. Shown as an error message to inform the user that the file is too large. The purpose is to clearly communicate that file size is the reason for the validation failure.',
	},
	validationErrorFileNameMissing: {
		id: 'ai-mate.upload-files-to-conversation.validation-error-file-name-missing',
		defaultMessage: 'File name is missing',
		description:
			'Error message displayed in a notification or validation error list when a user attempts to upload a file without a name to the AI conversation. Shown as an error message to inform the user that the file must have a name. The purpose is to clearly communicate that a missing file name is the reason for the validation failure.',
	},
	validationErrorUnknownError: {
		id: 'ai-mate.upload-files-to-conversation.validation-error-unknown-error',
		defaultMessage: 'Fail to validate file for unknown reason',
		description:
			'Error message displayed in a notification or validation error list when file validation fails for an unknown or unclassified reason during upload to the AI conversation. Shown as an error message to inform the user that validation failed but the specific cause could not be determined. The purpose is to provide a fallback error message when the system cannot identify the specific validation failure reason.',
	},
	validationErrorMissingErrorCode: {
		id: 'ai-mate.upload-files-to-conversation.validation-error-missing-error-code',
		defaultMessage: 'Validation result unknown',
		description:
			'Error message displayed in a notification or validation error list when the validation service returns a response without a proper error code during file upload to the AI conversation. Shown as an error message to inform the user that the validation status could not be determined. The purpose is to communicate that the validation system returned an incomplete or malformed response.',
	},
	validationErrorNotFound: {
		id: 'ai-mate.upload-files-to-conversation.validation-error-not-found',
		defaultMessage: 'Validation result is not found',
		description:
			'Error message displayed in a notification or validation error list when the system cannot retrieve the validation result for an uploaded file in the AI conversation, typically due to the validation request being lost or the result expiring. Shown as an error message to inform the user that the validation status could not be retrieved. The purpose is to communicate that the validation result is missing or unavailable.',
	},
});
export default _default_1;
